import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { isSuperAdmin } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { BookOpen, TrendingUp, Scale, ChevronRight, Database, Loader2 } from 'lucide-react';

const fmtCurrency = (v) => {
  const abs = Math.abs(v || 0);
  const formatted = abs.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
  return (v || 0) < 0 ? `(${formatted})` : formatted;
};

const getFYOptions = () => {
  const now = new Date();
  const currentYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const options = [];
  for (let i = 0; i < 5; i++) {
    const sy = currentYear - i;
    options.push({ label: `FY ${String(sy).slice(2)}-${String(sy + 1).slice(2)}`, value: sy });
  }
  return options;
};

const TallyTable = ({ title, debitSide, creditSide, debitTotal, creditTotal, debitLabel = "Debit", creditLabel = "Credit" }) => (
  <div className="border border-gray-300 rounded-lg overflow-hidden" data-testid={`tally-table-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="bg-slate-800 text-white px-4 py-2.5 text-center font-bold text-sm tracking-wide">{title}</div>
    <div className="grid grid-cols-2 divide-x divide-gray-300">
      {/* Debit Side */}
      <div>
        <div className="bg-slate-100 px-4 py-2 text-xs font-bold text-slate-700 border-b border-gray-300 flex justify-between">
          <span>{debitLabel}</span>
          <span>Amount</span>
        </div>
        <div className="divide-y divide-gray-100">
          {debitSide.map((item, i) => (
            <div key={i} className={`px-4 py-2 flex justify-between items-center text-sm ${item.bold ? 'font-bold bg-slate-50' : ''} ${item.indent ? 'pl-8 text-gray-500 text-xs' : ''}`}>
              <span className={item.bold ? 'text-slate-800' : 'text-gray-700'}>{item.particulars}</span>
              <span className={`font-mono tabular-nums ${item.amount < 0 ? 'text-red-600' : item.bold ? 'text-slate-900' : 'text-gray-800'}`}>
                {fmtCurrency(item.amount)}
              </span>
            </div>
          ))}
        </div>
        <div className="bg-slate-800 text-white px-4 py-2 flex justify-between font-bold text-sm border-t border-slate-700">
          <span>Total</span>
          <span className="font-mono tabular-nums">{fmtCurrency(debitTotal)}</span>
        </div>
      </div>
      {/* Credit Side */}
      <div>
        <div className="bg-slate-100 px-4 py-2 text-xs font-bold text-slate-700 border-b border-gray-300 flex justify-between">
          <span>{creditLabel}</span>
          <span>Amount</span>
        </div>
        <div className="divide-y divide-gray-100">
          {creditSide.map((item, i) => (
            <div key={i} className={`px-4 py-2 flex justify-between items-center text-sm ${item.bold ? 'font-bold bg-slate-50' : ''} ${item.indent ? 'pl-8 text-gray-500 text-xs' : ''}`}>
              <span className={item.bold ? 'text-slate-800' : 'text-gray-700'}>{item.particulars}</span>
              <span className={`font-mono tabular-nums ${item.amount < 0 ? 'text-red-600' : item.bold ? 'text-slate-900' : 'text-gray-800'}`}>
                {fmtCurrency(item.amount)}
              </span>
            </div>
          ))}
        </div>
        <div className="bg-slate-800 text-white px-4 py-2 flex justify-between font-bold text-sm border-t border-slate-700">
          <span>Total</span>
          <span className="font-mono tabular-nums">{fmtCurrency(creditTotal)}</span>
        </div>
      </div>
    </div>
  </div>
);

const FinancialStatements = ({ user }) => {
  const [fyOptions] = useState(getFYOptions());
  const [activeFY, setActiveFY] = useState(fyOptions[0].value);
  const [activeTab, setActiveTab] = useState('trading');
  const [trading, setTrading] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [challanVis, setChallanVis] = useState(null);
  const [visLoading, setVisLoading] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [backfillStarting, setBackfillStarting] = useState(false);

  const loadTrading = useCallback(async (fy) => {
    setLoading(true);
    try {
      const res = await api.get(`/financial/trading-account?fy_year=${fy}`);
      setTrading(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Trading Account');
    } finally { setLoading(false); }
  }, []);

  const loadPnl = useCallback(async (fy) => {
    setLoading(true);
    try {
      const res = await api.get(`/financial/profit-loss?fy_year=${fy}`);
      setPnl(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load P&L');
    } finally { setLoading(false); }
  }, []);

  const loadBalanceSheet = useCallback(async (fy) => {
    setLoading(true);
    try {
      const res = await api.get(`/financial/balance-sheet?fy_year=${fy}`);
      setBalanceSheet(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Balance Sheet');
    } finally { setLoading(false); }
  }, []);

  const loadChallanVisibility = useCallback(async () => {
    try {
      const res = await api.get('/settings/challan-visibility');
      setChallanVis(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (activeTab === 'trading') loadTrading(activeFY);
    else if (activeTab === 'pnl') loadPnl(activeFY);
    else if (activeTab === 'balance') loadBalanceSheet(activeFY);
    else if (activeTab === 'challan') loadChallanVisibility();
  }, [activeTab, activeFY, loadTrading, loadPnl, loadBalanceSheet, loadChallanVisibility]);

  const toggleChallanVisibility = async (key) => {
    if (!challanVis) return;
    setVisLoading(true);
    try {
      const newVal = !challanVis[key];
      await api.put('/settings/challan-visibility', { [key]: newVal });
      setChallanVis(prev => ({ ...prev, [key]: newVal }));
      toast.success(`${key.replace(/_/g, ' ').replace('visible', '')} visibility ${newVal ? 'enabled' : 'hidden'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setVisLoading(false); }
  };

  const fetchBackfillStatus = useCallback(async () => {
    try {
      const res = await api.get('/snapshots/backfill/status');
      setBackfillStatus(res.data);
      return res.data;
    } catch { return null; }
  }, []);

  // Poll while backfill is running
  useEffect(() => {
    if (backfillStatus?.state !== 'running') return undefined;
    const id = setInterval(fetchBackfillStatus, 1500);
    return () => clearInterval(id);
  }, [backfillStatus?.state, fetchBackfillStatus]);

  useEffect(() => { fetchBackfillStatus(); }, [fetchBackfillStatus]);

  const startBackfill = async () => {
    setBackfillStarting(true);
    try {
      await api.post('/snapshots/backfill?overwrite=true');
      toast.success('Stock history rebuild started');
      await fetchBackfillStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start rebuild');
    } finally {
      setBackfillStarting(false);
    }
  };

  if (!isSuperAdmin(user?.role)) {
    return <div className="p-8 text-center text-gray-500" data-testid="financial-statements-denied">Super Admin access required</div>;
  }

  const tabs = [
    { key: 'trading', label: 'Trading A/c', icon: BookOpen },
    { key: 'pnl', label: 'Profit & Loss', icon: TrendingUp },
    { key: 'balance', label: 'Balance Sheet', icon: Scale },
    { key: 'challan', label: 'Challan Visibility', icon: ChevronRight },
  ];

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="financial-statements-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Financial Statements</h1>
          <p className="text-gray-600 mt-1">Trading Account, P&L, and Balance Sheet</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {backfillStatus?.state === 'running' ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-sm" data-testid="backfill-progress">
              <Loader2 className="w-4 h-4 animate-spin text-amber-700" />
              <span className="text-amber-900 font-medium">
                Rebuilding stock history… {backfillStatus.done}/{backfillStatus.total} ({backfillStatus.progress}%)
              </span>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={startBackfill}
              disabled={backfillStarting}
              data-testid="rebuild-stock-history-btn"
              className="border-slate-300"
              title="Recompute daily stock snapshots from oldest transaction to today"
            >
              <Database className="w-4 h-4 mr-2" />
              {backfillStarting ? 'Starting…' : 'Rebuild Stock History'}
            </Button>
          )}
          {backfillStatus?.state === 'completed' && backfillStatus?.total > 0 && (
            <span className="text-xs text-emerald-700" data-testid="backfill-last-status">
              ✓ Last rebuild: {backfillStatus.total} days
            </span>
          )}
        </div>
      </div>

      {/* Tabs + FY Selector */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex gap-1 border-b pb-3 mb-1">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === t.key ? 'bg-slate-800 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                data-testid={`fs-tab-${t.key}`}>
                <t.icon className="w-4 h-4" />{t.label}
              </button>
            ))}
          </div>
          {activeTab !== 'challan' && (
            <div className="flex flex-wrap gap-1 items-center">
              {fyOptions.map(fy => (
                <Button key={fy.value} size="sm"
                  variant={activeFY === fy.value ? 'default' : 'outline'}
                  className={activeFY === fy.value ? 'bg-slate-800 hover:bg-slate-900' : ''}
                  onClick={() => setActiveFY(fy.value)} data-testid={`fs-fy-${fy.value}`}>
                  {fy.label}
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {loading && <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-700"></div></div>}

      {/* Trading Account */}
      {activeTab === 'trading' && trading && !loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Net Sales</p><p className="text-lg font-bold text-blue-700" data-testid="fs-net-sales">{fmtCurrency(trading.totals.net_sales)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Net Purchases</p><p className="text-lg font-bold text-orange-700" data-testid="fs-net-purchases">{fmtCurrency(trading.totals.net_purchases)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Closing Stock</p><p className="text-lg font-bold text-emerald-700" data-testid="fs-closing-stock-card">{fmtCurrency(trading.totals.closing_stock)}</p></CardContent></Card>
            <Card className={`border-2 ${trading.gross_profit >= 0 ? 'border-emerald-300 bg-emerald-50/50' : 'border-red-300 bg-red-50/50'}`}>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-500">{trading.gross_profit >= 0 ? 'Gross Profit' : 'Gross Loss'}</p>
                <p className={`text-lg font-bold ${trading.gross_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="fs-gross-profit">{fmtCurrency(trading.gross_profit)}</p>
              </CardContent>
            </Card>
          </div>
          <TallyTable
            title={`Trading Account for ${trading.period}`}
            debitSide={trading.debit_side}
            creditSide={trading.credit_side}
            debitTotal={(trading.totals.opening_stock || 0) + trading.totals.net_purchases + (trading.gross_profit > 0 ? trading.gross_profit : 0)}
            creditTotal={trading.totals.net_sales + trading.totals.closing_stock + (trading.gross_profit < 0 ? Math.abs(trading.gross_profit) : 0)}
          />
        </div>
      )}

      {/* P&L */}
      {activeTab === 'pnl' && pnl && !loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Gross Profit</p><p className="text-lg font-bold text-emerald-700">{fmtCurrency(pnl.gross_profit)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Total Expenses</p><p className="text-lg font-bold text-red-600">{fmtCurrency(pnl.totals.total_expenses)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Commissions Paid</p><p className="text-lg font-bold text-amber-700">{fmtCurrency(pnl.totals.commissions_paid)}</p></CardContent></Card>
            <Card className={`border-2 ${pnl.net_profit >= 0 ? 'border-emerald-300 bg-emerald-50/50' : 'border-red-300 bg-red-50/50'}`}>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-500">{pnl.net_profit >= 0 ? 'Net Profit' : 'Net Loss'}</p>
                <p className={`text-lg font-bold ${pnl.net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="fs-net-profit">{fmtCurrency(pnl.net_profit)}</p>
              </CardContent>
            </Card>
          </div>
          <TallyTable
            title={`Profit & Loss Account for ${pnl.period}`}
            debitSide={pnl.debit_side}
            creditSide={pnl.credit_side}
            debitTotal={pnl.totals.total_expenses + (pnl.net_profit > 0 ? pnl.net_profit : 0)}
            creditTotal={pnl.gross_profit + (pnl.net_profit < 0 ? Math.abs(pnl.net_profit) : 0)}
          />
          {pnl.expense_by_category?.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold text-sm text-gray-800 mb-3">Expense Breakdown by Category</h3>
                <div className="space-y-2">
                  {pnl.expense_by_category.map((c, i) => (
                    <div key={i} className="flex justify-between items-center text-sm border-b border-gray-100 pb-1">
                      <span className="text-gray-700">{c.category}</span>
                      <span className="font-mono font-medium text-gray-800">{fmtCurrency(c.amount)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Balance Sheet */}
      {activeTab === 'balance' && balanceSheet && !loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Receivables</p><p className="text-lg font-bold text-blue-700">{fmtCurrency(balanceSheet.totals.receivables)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Closing Stock</p><p className="text-lg font-bold text-emerald-700">{fmtCurrency(balanceSheet.totals.closing_stock)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Payables</p><p className="text-lg font-bold text-red-600">{fmtCurrency(balanceSheet.totals.payables)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Capital</p><p className="text-lg font-bold text-slate-700">{fmtCurrency(balanceSheet.totals.capital)}</p></CardContent></Card>
          </div>
          <TallyTable
            title={`Balance Sheet ${balanceSheet.period}`}
            debitLabel="Liabilities"
            creditLabel="Assets"
            debitSide={balanceSheet.liabilities}
            creditSide={balanceSheet.assets}
            debitTotal={balanceSheet.totals.total_liabilities}
            creditTotal={balanceSheet.totals.total_assets}
          />
        </div>
      )}

      {/* Challan Visibility */}
      {activeTab === 'challan' && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div>
              <h3 className="font-bold text-lg text-gray-900">Challan Visibility Settings</h3>
              <p className="text-sm text-gray-500 mt-1">Control which challan-type transactions are visible across the system</p>
            </div>
            {challanVis ? (
              <div className="space-y-3" data-testid="challan-visibility-settings">
                {[
                  { key: 'se_challan_visible', label: 'Sales Entry (Challan)', desc: 'Show challan-type sales entries in SE lists and reports' },
                  { key: 'pe_challan_visible', label: 'Purchase Entry (Challan)', desc: 'Show challan-type purchase entries in PE lists and reports' },
                  { key: 'sr_challan_visible', label: 'Sales Return (Challan)', desc: 'Show challan-type sales returns' },
                  { key: 'pr_challan_visible', label: 'Purchase Return (Challan)', desc: 'Show challan-type purchase returns' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div>
                      <p className="font-medium text-sm text-gray-800">{item.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-medium ${challanVis[item.key] ? 'text-emerald-600' : 'text-red-500'}`}>
                        {challanVis[item.key] ? 'Visible' : 'Hidden'}
                      </span>
                      <Switch
                        checked={challanVis[item.key]}
                        disabled={visLoading}
                        onCheckedChange={() => toggleChallanVisibility(item.key)}
                        data-testid={`toggle-${item.key}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">Loading settings...</div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FinancialStatements;
